import { useCallback, useEffect } from 'react'
import { X } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { TIERS } from '@mindweb/shared'
import type { UserProfile } from '@mindweb/shared'
import { Navbar } from '@/components/ui/navbar'
import { Seo } from '@/components/seo/seo'
import { PricingCard } from '@/components/billing/pricing-card'
import { useAuthStore } from '@/lib/store/auth'
import { api } from '@/lib/api'

export function PricingPage() {
  const user = useAuthStore((s) => s.user)
  const setUser = useAuthStore((s) => s.setUser)
  const { t } = useTranslation()

  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)

  const refreshProfile = useCallback(async () => {
    await api.post('/api/billing/sync')
    const r = await api.get<UserProfile>('/api/me')
    if (r.success && r.data) {
      setUser(r.data)
    }
  }, [setUser])

  useEffect(() => {
    if (isAuthenticated) {
      refreshProfile()
    }
  }, [isAuthenticated, refreshProfile])

  const navigate = useNavigate()

  return (
    <div className="min-h-screen bg-background">
      <Seo title={t('seo.pricing.title')} description={t('seo.pricing.description')} path="/pricing" />
      <Navbar />

      <main className="relative mx-auto max-w-4xl px-6 py-16">
        <button
          onClick={() => navigate(-1)}
          className="absolute top-4 right-6 p-2 rounded-full bg-muted/80 hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
          aria-label={t('pricing.goBack')}
        >
          <X className="h-5 w-5" />
        </button>

        <div className="text-center mb-12">
          <h1 className="text-3xl font-bold text-foreground">{t('pricing.title')}</h1>
          <p className="mt-3 text-muted-foreground">
            {t('pricing.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl mx-auto">
          {(Object.entries(TIERS) as Array<[string, typeof TIERS[keyof typeof TIERS]]>).map(
            ([key, tier]) => (
              <PricingCard
                key={key}
                tierKey={key}
                name={tier.name}
                price={tier.price}
                maxGraphs={tier.maxGraphs}
                maxNodesPerGraph={tier.maxNodesPerGraph}
                highlighted={key === 'pro'}
                currentTier={user?.tier ?? 'free'}
                cancelAtPeriodEnd={user?.cancelAtPeriodEnd ?? false}
                pendingTier={user?.pendingTier ?? null}
                onAction={refreshProfile}
              />
            ),
          )}
        </div>
      </main>
    </div>
  )
}
